import { Fragment, useState, useCallback, MouseEvent, ChangeEvent, FC } from "react";
import { Button, Menu } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../assets/checkbox-checked.icon.svg";
import { MenuItem, SectionItem } from "../../common/interfaces/comps/menu-item.interface";
import { ImageCheckbox } from "../checkbox/checkbox.comp";

export interface Action {
    name: string;
    action: () => void;
}

export const ActionMenu = ({
    label,
    actions,
    disabled = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    actions: Action[];
    disabled?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleMenuItem = (action: () => void) => {
        action();
        handleMenuClose();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                disabled={disabled}
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {actions.map(action => (
                    <div
                        key={action.name}
                        className="dropdown-menu-item"
                        onClick={() => handleMenuItem(action.action)}
                    >
                        {action.name}
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const CheckboxMenuFilter = ({
    label,
    list,
    checkedList,
    setCheckedList,
    showCounter = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    list: string[];
    checkedList: string[];
    setCheckedList: (arg1: string[]) => void;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleChangeCheckedItem = useCallback(
        (item: string) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item);
            index !== -1 ? newList.splice(index, 1) : newList.push(item);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && <div className="menu-button__counter">{checkedList.length}</div>}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <div key={item} className="dropdown-menu-item--checkbox">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkedList.includes(item)}
                                    onChange={() => handleChangeCheckedItem(item)}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                />
                            }
                            label={item}
                        />
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const CheckboxMenuFilterT = <T,>({
    label,
    list,
    checkedList,
    setCheckedList,
    showCounter = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    list: MenuItem<T>[];
    checkedList: MenuItem<T>[];
    setCheckedList: (arg1: MenuItem<T>[]) => void;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleChangeCheckedItem = useCallback(
        (item: MenuItem<T>) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item);
            index !== -1 ? newList.splice(index, 1) : newList.push(item);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && <div className="menu-button__counter">{checkedList.length}</div>}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <div key={crypto.randomUUID()} className="dropdown-menu-item--checkbox">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkedList.includes(item)}
                                    onChange={() => handleChangeCheckedItem(item)}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                />
                            }
                            label={item.label}
                        />
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const MultipleCheckboxMenuFilter = <T,>({
    label,
    multipleList,
    checkedList,
    setCheckedList,
    startIcon,
    showCounter = false,
    rightPosition = false,
    minWidth
}: {
    label: string;
    multipleList: SectionItem<T>[];
    checkedList: T[];
    setCheckedList: (arg1: T[]) => void;
    startIcon: FC<React.SVGProps<SVGSVGElement>>;
    showCounter?: boolean;
    rightPosition?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);
    const [searchString, setSearchString] = useState<string>("");

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setSearchString(val ?? "");
    };

    const handleChangeCheckedItem = useCallback(
        (item: MenuItem<T>) => {
            const newList = [...checkedList];
            const index = newList.indexOf(item.value);
            index !== -1 ? newList.splice(index, 1) : newList.push(item.value);
            setCheckedList(newList);
        },
        [checkedList, setCheckedList]
    );

    const handleSelectUnselectAll = useCallback(
        (selectAll: boolean) => {
            if (selectAll) {
                const newList = [...checkedList];
                multipleList.forEach(sectionItem => {
                    sectionItem.list.forEach(item => {
                        const index = newList.indexOf(item.value);
                        if (index === -1) {
                            newList.push(item.value);
                        }
                    });
                });
                setCheckedList(newList);
            } else {
                setCheckedList([]);
            }
        },
        [multipleList, checkedList, setCheckedList]
    );

    const isAllSelected = useCallback(() => {
        return !multipleList.find(sectionItem =>
            sectionItem.list.find(item => !checkedList.includes(item.value))
        );
    }, [multipleList, checkedList]);

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                startIcon={<SvgIcon component={startIcon} inheritViewBox />}
                endIcon={
                    <SvgIcon
                        component={menuOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                onClick={handleMenuClick}
            >
                <span className="menu-button__label">{label}</span>
                {showCounter && <div className="menu-button__counter">{checkedList.length}</div>}
            </Button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                <div className="dropdown-menu-search">
                    <input
                        type="text"
                        placeholder="Search"
                        onChange={handleSearchChange}
                        value={searchString}
                    />
                </div>
                <div className="dropdown-menu-item--checkbox">
                    <FormControlLabel
                        control={<ImageCheckbox checked={isAllSelected()} />}
                        onClick={() => handleSelectUnselectAll(!isAllSelected())}
                        label={"Unselect / Select All"}
                    />
                </div>
                {multipleList.map(listItem => (
                    <div key={crypto.randomUUID()} className="dropdown-menu-group">
                        {listItem.list.filter(item =>
                            item.label
                                .toLocaleLowerCase()
                                .includes(searchString.toLocaleLowerCase())
                        ).length > 0 && (
                            <div className="dropdown-menu-section">{listItem.title}</div>
                        )}
                        {listItem.list
                            .filter(item =>
                                item.label
                                    .toLocaleLowerCase()
                                    .includes(searchString.toLocaleLowerCase())
                            )
                            .map(item => (
                                <div
                                    key={crypto.randomUUID()}
                                    className="dropdown-menu-item--checkbox"
                                >
                                    <FormControlLabel
                                        control={
                                            <ImageCheckbox
                                                checked={checkedList.includes(item.value)}
                                            />
                                        }
                                        onClick={() => handleChangeCheckedItem(item)}
                                        label={item.label}
                                    />
                                </div>
                            ))}
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const SelectionFilter = ({
    list,
    selectedItem,
    setSelectedItem,
    rightPosition = false,
    disabled = false,
    minWidth
}: {
    list: string[];
    selectedItem: string;
    setSelectedItem: (arg1: string) => void;
    rightPosition?: boolean;
    disabled?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [selectAnchor, setSelectAnchor] = useState<Element | null>(null);
    const selectOpen = Boolean(selectAnchor);

    const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
        setSelectAnchor(event.currentTarget);
    };

    const handleSelectClose = () => {
        setSelectAnchor(null);
    };

    const handleSelectItem = (item: string) => {
        setSelectedItem(item);
        handleSelectClose();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                disabled={disabled}
                endIcon={
                    <SvgIcon
                        component={selectOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                onClick={handleSelectClick}
            >
                <span className="menu-button__label">{selectedItem}</span>
            </Button>
            <Menu
                anchorEl={selectAnchor}
                open={selectOpen}
                onClose={handleSelectClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <div
                        key={item}
                        className={`dropdown-menu-item${item === selectedItem ? " dropdown-menu-item--selected" : ""}`}
                        onClick={() => handleSelectItem(item)}
                    >
                        {item}
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};

export const SelectionFilterT = <T,>({
    list,
    selectedItem,
    setSelectedItem,
    rightPosition = false,
    disabled = false,
    minWidth
}: {
    list: MenuItem<T>[];
    selectedItem: MenuItem<T>;
    setSelectedItem: (arg1: MenuItem<T>) => void;
    rightPosition?: boolean;
    disabled?: boolean;
    minWidth?: number;
}) => {
    const menuStyles = minWidth ? { minWidth: `${minWidth}px` } : {};
    const [selectAnchor, setSelectAnchor] = useState<Element | null>(null);
    const selectOpen = Boolean(selectAnchor);

    const handleSelectClick = (event: MouseEvent<HTMLElement>) => {
        setSelectAnchor(event.currentTarget);
    };

    const handleSelectClose = () => {
        setSelectAnchor(null);
    };

    const handleSelectItem = (item: MenuItem<T>) => {
        setSelectedItem(item);
        handleSelectClose();
    };

    return (
        <Fragment>
            <Button
                className="menu-button"
                variant="text"
                disabled={disabled}
                endIcon={
                    <SvgIcon
                        component={selectOpen ? KeyboardArrowUpIcon : KeyboardArrowDownIcon}
                        inheritViewBox
                    />
                }
                onClick={handleSelectClick}
            >
                <span className="menu-button__label">{selectedItem.label}</span>
            </Button>
            <Menu
                anchorEl={selectAnchor}
                open={selectOpen}
                onClose={handleSelectClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "dropdown-menu",
                        sx: menuStyles
                    }
                }}
                transformOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "top" }}
                anchorOrigin={{ horizontal: rightPosition ? "right" : "left", vertical: "bottom" }}
            >
                {list.map(item => (
                    <div
                        key={crypto.randomUUID()}
                        className={`dropdown-menu-item${item === selectedItem ? " dropdown-menu-item--selected" : ""}`}
                        onClick={() => handleSelectItem(item)}
                    >
                        {item.label}
                    </div>
                ))}
            </Menu>
        </Fragment>
    );
};
